import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { set } from '@ember/object';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import {
  HdsModal,
  HdsTextDisplay,
  HdsFormTextInputField,
  HdsButtonSet,
  HdsButton,
  HdsSeparator,
  HdsAlert,
  HdsTextBody,
  HdsCopySnippet,
} from '@hashicorp/design-system-components/components';
import { WithErrors } from 'core/components';
import { boxPadding } from 'core/modifiers';

/**
 * `ScimTokensCreate` is a modal component containing logic to create a token for SCIM.
 *
 *
 * ```
 * <Scim::Tokens::Create
 *   @onCancel={{this.closeTokenModal}}
 *   @organization={{@organization}}
 * />
 * ```
 *
 * @class ScimTokenCreate
 *
 */
export default class ScimTokensCreate extends Component {
  @service api;
  @service flashMessages;
  @service intl;

  @tracked createTokenError = false;
  @tracked createTokenErrorDescription = this.intl.t(
    'components.scim.tokens.create.modal.alert.error-creating-token-description'
  );
  @tracked modalStepOne = true;
  @tracked token = '';
  @tracked tokenDisplayName = '';

  @tracked form = {
    fields: {
      name: '',
    },
    validationErrors: {
      name: [],
    },
    fieldValidators: {
      name: [
        {
          message: this.intl.t(
            'components.scim.tokens.create.modal.form.error.empty'
          ),
          isValid: (v) => v && v !== '',
          subValidators: [
            {
              message: this.intl.t(
                'components.scim.tokens.create.modal.form.error.alphanumeric-start'
              ),
              isValid: (v) => this.nameStartsWithAlphanumeric.test(v),
            },
            {
              message: this.intl.t(
                'components.scim.tokens.create.modal.form.error.single-spaces'
              ),
              isValid: (v) => !this.nameContainsOnlySingleSpaces.test(v),
            },
            {
              message: this.intl.t(
                'components.scim.tokens.create.modal.form.error.alphanumeric-end'
              ),
              isValid: (v) => this.nameEndsWithAlphanumeric.test(v),
            },
            {
              message: this.intl.t(
                'components.scim.tokens.create.modal.form.error.invalid'
              ),
              isValid: (v) => !this.nameInvalidCharacters.test(v),
            },
            {
              message: this.intl.t(
                'components.scim.tokens.create.modal.form.error.invalid-length'
              ),
              isValid: (v) => v.length >= 3 && v.length <= 128,
            },
          ],
        },
      ],
    },
  };

  // Starts with a letter or number.
  nameStartsWithAlphanumeric = /^[a-z0-9]+/i;

  // Only uses one space between words
  nameContainsOnlySingleSpaces = /^.*\s{2,}.*$/;

  // Ends with a letter or number.
  nameEndsWithAlphanumeric = /[a-z0-9]+$/i;

  // May only contain letters, numbers, hypens, underscores, periods, or spaces between words.
  nameInvalidCharacters = /[^a-z0-9_\-.\s]/i;

  setName = (evt) => {
    this.form.fields.name = evt.target.value;
    this.validateForm();
  };

  validateForm({ name = false } = { name: true }) {
    if (name) {
      let nameErrors = this.runValidators(
        this.form.fieldValidators.name,
        this.form.fields.name
      );
      set(
        this.form,
        'validationErrors.name',
        nameErrors.map((v) => v.message)
      );
    }

    return !Object.keys(this.form.validationErrors).find(
      (k) => this.form.validationErrors[k].length
    );
  }

  runValidators(validators, value) {
    let failedValidators = validators.flatMap((v) => {
      if (!v.isValid(value)) {
        return v;
      }

      if (v.subValidators) {
        return this.runValidators(v.subValidators, value);
      }

      return [];
    });
    return failedValidators;
  }

  onCreateError = async (e) => {
    this.modalStepOne = false;
    this.createTokenError = true;
    const status = e?.status;

    if (macroCondition(isDevelopingApp())) {
      console.error(e);
    }

    // Handle different errors beyond the default
    switch (status) {
      case 409: {
        this.createTokenErrorDescription = this.intl.t(
          'components.scim.tokens.create.modal.alert.error-creating-token-already-exists'
        );
        break;
      }
    }
  };

  createToken = task(async (evt) => {
    evt.preventDefault();

    if (!this.validateForm({ name: true })) return;

    const { organization } = this.args;

    try {
      let { token } = await this.api.scim.scimServiceCreateScimToken(
        organization.id,
        { displayName: this.form.fields.name }
      );
      this.token = token;
      this.tokenDisplayName = this.form.fields.name;
      this.flashMessages.success(
        this.intl.t('components.scim.tokens.create.success')
      );
    } catch (e) {
      await this.onCreateError(e);
    }
    this.modalStepOne = false;
  });

  restartModal = () => {
    this.modalStepOne = true;
  };

  <template>
    <HdsModal @onClose={{@onCancel}} data-test-scim-tokens-create as |M|>
      <M.Header @icon="token">
        {{t "components.scim.tokens.create.modal.header"}}
      </M.Header>
      {{#if this.modalStepOne}}
        <M.Body>
          <HdsTextDisplay
            @tag="div"
            @size="300"
            @weight="bold"
            {{boxPadding "0 0 sm 0"}}
          >
            {{t "components.scim.tokens.create.modal.title"}}
          </HdsTextDisplay>
          <WithErrors as |FormError FieldError|>
            <form
              id="create-token"
              novalidate
              {{on "submit" this.createToken.perform}}
            >
              <FieldError
                @field="display-name"
                @hdsDelegatedError={{true}}
                as |err|
              >
                <HdsFormTextInputField
                  @id="display-name"
                  @isInvalid={{or err this.form.validationErrors.name.length}}
                  @type="text"
                  @value={{this.form.fields.name}}
                  autocomplete="off"
                  {{on "input" this.setName}}
                  data-test-scim-tokens-create-display-name-input
                  as |F|
                >
                  <F.Label data-test-scim-tokens-create-display-name-label>
                    {{t "components.scim.tokens.create.modal.form.label"}}
                  </F.Label>
                  <F.HelperText
                    data-test-scim-tokens-create-display-name-help-text
                  >
                    {{t "components.scim.tokens.create.modal.form.help-text"}}
                  </F.HelperText>
                  {{#if err}}
                    <F.Error data-test-scim-tokens-create-error>
                      {{err.description}}
                    </F.Error>
                  {{else if this.form.validationErrors.name.length}}
                    <F.Error data-test-validation-name-error as |E|>
                      {{#each this.form.validationErrors.name as |msg|}}
                        <E.Message>
                          {{msg}}
                        </E.Message>
                      {{/each}}
                    </F.Error>
                  {{/if}}
                </HdsFormTextInputField>
              </FieldError>
            </form>
          </WithErrors>
        </M.Body>
        <M.Footer as |F|>
          <HdsButtonSet>
            <HdsButton
              @text={{t "components.scim.tokens.create.modal.actions.next"}}
              {{on "click" this.createToken.perform}}
              data-test-scim-tokens-create-next
            />
            <HdsButton
              @color="secondary"
              @text={{t "components.scim.tokens.create.modal.actions.cancel"}}
              {{on "click" F.close}}
            />
          </HdsButtonSet>
        </M.Footer>
      {{else}}
        <M.Body>
          <HdsTextDisplay
            @tag="div"
            @size="300"
            @weight="bold"
            {{boxPadding "0 0 sm 0"}}
          >
            {{t "components.scim.tokens.create.modal.title"}}
          </HdsTextDisplay>
          <HdsFormTextInputField
            @id="display-name"
            name="display-name"
            placeholder={{this.tokenDisplayName}}
            disabled
            data-test-scim-tokens-create-disabled-input
            as |F|
          >
            <F.Label>
              {{t "components.scim.tokens.create.modal.form.label"}}
            </F.Label>
            <F.HelperText>
              {{t "components.scim.tokens.create.modal.form.help-text"}}
            </F.HelperText>
          </HdsFormTextInputField>
          <HdsSeparator />
          <HdsTextDisplay
            @tag="div"
            @size="300"
            @weight="bold"
            {{boxPadding "0 0 sm 0"}}
          >
            {{t "components.scim.tokens.create.modal.generated"}}
          </HdsTextDisplay>
          {{#if this.createTokenError}}
            <HdsAlert
              @color="critical"
              @type="inline"
              data-test-scim-tokens-create-error
              as |A|
            >
              <A.Title>
                {{t
                  "components.scim.tokens.create.modal.alert.error-creating-token"
                }}
              </A.Title>
              <A.Description>
                {{this.createTokenErrorDescription}}
              </A.Description>
              <A.Button
                @color="tertiary"
                @icon="repeat"
                @text={{t
                  "components.scim.tokens.create.modal.alert.generate-token-again"
                }}
                {{on "click" this.restartModal}}
                data-test-scim-tokens-create-error-link
              />
            </HdsAlert>
          {{else}}
            <HdsAlert @type="inline" @color="warning" as |A|>
              <A.Title>
                {{t "components.scim.tokens.create.modal.alert.success.title"}}
              </A.Title>
              <A.Description
                data-test-scim-tokens-create-success-alert-description
              >
                {{t
                  "components.scim.tokens.create.modal.alert.success.description"
                }}
              </A.Description>
            </HdsAlert>
            <HdsTextBody
              @tag="p"
              @size="200"
              @weight="semibold"
              {{boxPadding "md 0 2xs 2xs"}}
            >
              {{t "components.scim.tokens.create.modal.actions.copy"}}
            </HdsTextBody>
            <HdsCopySnippet
              @color="secondary"
              @container=".hds-modal"
              @isTruncated={{true}}
              @textToCopy={{this.token}}
              data-test-scim-tokens-create-success-alert-token
            />
          {{/if}}
        </M.Body>
        <M.Footer as |F|>
          <HdsButton
            @text={{t "components.scim.tokens.create.modal.actions.done"}}
            {{on "click" F.close}}
          />
        </M.Footer>
      {{/if}}
    </HdsModal>
  </template>
}
